import { BookingService } from "@/services/BookingService";
import { occursOnDate } from "@/lib/recurrence";
import type { Booking } from "@/types/Booking";
import type { CanBookValidationResponse } from "@/types/CanBookValidation";
import type { Member } from "@/types/Member";
import { endTimeToMinutes } from "@/utils/bookingTime";

export type BookingAvailabilityStatus = "available" | "warning" | "blocked";

/**
 * Derive the overall availability status shown on the RepresentativeBookingFlow Availability screen.
 *
 * CRITICAL RULE: hard conflicts — a room core/time overlap, a cross-room same-member overlap, an
 * amenity double-book, or a recurring-occurrence clash (R6/R7) — are non-negotiable. The server
 * rejects them with a 409 for EVERY role, so they must always be "blocked", never downgraded to a
 * bypassable "warning" for elevated users. (The old logic folded them into a bypassable block, so a
 * PM could click "Continue" only for the submit to fail.) Only genuinely acknowledgeable soft rules
 * — gap/continuous-chain warnings and the multi-room R22 acknowledgement — are "warning".
 */
export function deriveBookingAvailabilityStatus(params: {
  validationResponse: CanBookValidationResponse | null;
  hasError: boolean;
  canBypass: boolean;
}): BookingAvailabilityStatus {
  const { validationResponse, hasError, canBypass } = params;
  if (!validationResponse) return "blocked";
  if (hasError) return "blocked";

  const { validations, warnings, consecutiveBookingInfo } = validationResponse;

  const hasHardConflict =
    !validations.roomAvailability.valid ||
    !validations.crossRoomConflict.valid ||
    !validations.amenityAvailability.valid ||
    !validations.recurringConflicts.valid;

  const hasBlockingErrors =
    !validations.inputValidation.valid ||
    !validations.entityExistence.valid ||
    !validations.memberPermissions.valid ||
    !validations.slotBookingAuthorization.valid ||
    !validations.locationPermission.valid ||
    hasHardConflict;

  const hasWarnings = warnings.length > 0;
  const wouldCreateConsecutiveChain = consecutiveBookingInfo?.wouldCreateChain === true;
  const hasMultiRoomConflict = validationResponse.multiRoomConflict?.hasConflict === true;

  if (hasHardConflict) return "blocked"; // never bypassable
  if (hasBlockingErrors && !canBypass) return "blocked";
  if (hasBlockingErrors && canBypass) return "warning";
  if (hasWarnings) return "warning";
  if (wouldCreateConsecutiveChain) return "warning";
  if (hasMultiRoomConflict) return "warning";
  return "available";
}

/**
 * Convert time string (HHMM or HH:mm) to minutes since midnight
 */
export const timeToMinutes = (time: string): number => {
  const cleanTime = time.replace(":", "");
  const hours = parseInt(cleanTime.substring(0, 2), 10);
  const minutes = parseInt(cleanTime.substring(2, 4), 10);
  return hours * 60 + minutes;
};

/**
 * Format time from HHmm to HH:mm
 */
export const formatTimeWithColon = (time: string): string => {
  if (time.includes(":")) return time;
  if (time.length === 4) {
    return `${time.slice(0, 2)}:${time.slice(2)}`;
  }
  return time;
};

/**
 * Parse duration string (e.g., "30m", "1h00m", "2h00m") to minutes
 */
export const parseDuration = (duration: string): number => {
  const hoursMatch = duration.match(/(\d+)h/);
  const minutesMatch = duration.match(/(\d+)m/);
  const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
  return hours * 60 + minutes;
};

/**
 * Calculate end time given start time and duration in minutes
 * Returns time in HHmm format (e.g., "1430")
 */
export const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const hours = parseInt(startTime.slice(0, 2), 10);
  const minutes = parseInt(startTime.slice(2), 10);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  // Ending exactly at midnight is "2400" (end of THIS day) — "0000" reads as before the start.
  if (totalMinutes === 24 * 60) return "2400";
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${endHours.toString().padStart(2, "0")}${endMinutes.toString().padStart(2, "0")}`;
};

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface MemberConflictCheck {
  hasConflict: boolean;
  conflictingBooking?: Booking;
}

export interface RoomCollisionCheck {
  hasCollision: boolean;
  collisionType?: "core" | "buffer";
  conflictingBooking?: Booking;
}

export interface UnbookableGapCheck {
  hasGap: boolean;
  gapDurationMinutes?: number;
  gapBefore?: Booking;
  gapAfter?: Booking;
}

/**
 * A detected gap between the proposed booking and an adjacent booking.
 *
 * `warningType` is derived from who owns the adjacent booking:
 *  - `continuous` — the proposer owns both sides of the gap; the server chains the two bookings and bills
 *    the whole merged span (including the gap) whenever the raw gap is in `(0, CONTINUOUS_GAP_MAX_RAW_MINUTES]`
 *    (>0 and ≤90m) — see ember `findConsecutiveBookingChains` (no lower floor, buffer-blind). Because any
 *    billed gap costs the member money, the warning fires for ALL of them: if it's billed, it's warned. A
 *    gap over 90m is bookable by another member, so it is not chained/billed and does not warn; a zero raw
 *    gap (butting — no gap billed) is also silent.
 *  - `gap` — a different member owns the adjacent booking, so the gap is unusable by anyone else ONLY while
 *    it is too small to book: fires for raw gaps in `(0, MIN_BOOKABLE_GAP_RAW_MINUTES)` (>0 and strictly <90)
 *    with usable space > 0 (never billed). A gap of 90+ fits a 1h booking + two 15m buffers, so it is
 *    bookable and does not warn.
 *
 * `gapRawMinutes` is the raw end-to-start span (the user-facing "gap"). `gapUsableMinutes` is the
 * buffer-to-buffer remainder; kept for callers that still want it, but UI should display `gapRawMinutes` so
 * "0-min gap" messages disappear when buffers exactly consume the raw span.
 *
 * `isSameOrg` is true when the "continuous" case is triggered by org co-ownership (a DIFFERENT member in the
 * proposer's organisation owns the adjacent booking), rather than the proposer owning both bookings.
 */
export interface BookingGap {
  direction: "before" | "after";
  gapRawMinutes: number;
  gapUsableMinutes: number;
  adjacentBooking: Booking;
  warningType: "continuous" | "gap";
  /** True when the "continuous" case is triggered by org co-ownership (a DIFFERENT member in the
   *  proposer's organisation owns the adjacent booking), rather than the proposer owning both. */
  isSameOrg?: boolean;
}

export interface RecurringAvailabilityCheck {
  totalWeeks: number;
  availableWeeks: number;
  conflictDates: Date[];
}

const BUFFER_MINUTES = 15;
// R11 — same-member "continuous booking": the two bookings are chained and billed as one merged
// block for any raw gap up to 90 minutes, so the member is warned across that range (inclusive).
// A gap over 90 minutes leaves room for another member to book (1h + two 15m buffers), so it is
// neither bridged/billed nor warned. (2026-07-09: lowered 120 → 90 to align with bookability and
// stop billing members for gaps another member could have used — mirror server chain max.)
const CONTINUOUS_GAP_MAX_RAW_MINUTES = 90;
// R10 — cross-member "unbookable gap": only warn when the gap is TOO SMALL for another member to
// book. A standard 1-hour booking plus its two 15-minute buffers needs 90 minutes, so a gap of 90+
// minutes IS bookable and does not warn. Warn strictly under 90.
const MIN_BOOKABLE_GAP_RAW_MINUTES = 90;

/**
 * Check if member has a conflicting booking at this time in another room
 */
export const checkMemberConflict = async (
  memberId: string,
  date: Date,
  startTime: string,
  endTime: string,
  currentRoomId: string,
): Promise<MemberConflictCheck> => {
  const bookingService = new BookingService();
  const result = await bookingService.checkMemberAvailabilityAcrossRooms(
    memberId,
    date,
    formatTimeWithColon(startTime),
    formatTimeWithColon(endTime),
    currentRoomId,
  );

  return {
    hasConflict: result.hasConflict,
    conflictingBooking: result.conflictingBookings[0],
  };
};

/**
 * Check if member is active and allowed to make bookings
 * This check is skipped if the booking is being made by an elevated user for someone else
 */
export const checkMemberActive = (member: Member, bookingForSelf: boolean): ValidationResult => {
  console.log("Checking member active status:", { member, bookingForSelf });

  // If booking for someone else (by admin/PM), skip this check
  if (!bookingForSelf) {
    return { isValid: true, errors: [], warnings: [] };
  }

  const errors: string[] = [];

  if (!member.active) {
    errors.push("Member account is not active and cannot make bookings.");
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: [],
  };
};

/**
 * Check if booking collides with existing bookings in the same room
 * Considers 15-minute buffer before/after bookings
 */
export const checkRoomCollisions = (
  startTime: string,
  durationMinutes: number,
  bookings: Booking[],
  roomId: string,
  date: Date,
  memberId: string,
  // R6 parity — the booked-for member's organisation party (members + owner). When supplied, the
  // org's own bookings may butt back-to-back (buffer waived) exactly as the same member's may.
  // Omit for non-org bookings; behaviour is then unchanged.
  sameOrgMemberIds?: ReadonlySet<string>,
): RoomCollisionCheck => {
  const proposedStartMinutes = timeToMinutes(startTime);
  const proposedEndMinutes = proposedStartMinutes + durationMinutes;
  const proposedBufferStart = proposedStartMinutes - BUFFER_MINUTES;
  const proposedBufferEnd = proposedEndMinutes + BUFFER_MINUTES;

  // Filter to bookings that OCCUR on this date (recurrence-aware — see @/lib/recurrence)
  const relevantBookings = bookings.filter((b) => {
    if (b.isCancelled || b.roomId !== roomId) return false;
    return occursOnDate(b, date);
  });

  // Check each booking for conflicts
  for (const booking of relevantBookings) {
    const bookingStartMinutes = timeToMinutes(booking.startTime);
    const bookingEndMinutes = endTimeToMinutes(booking.endTime, booking.startTime);
    const hasBuffers = booking.hasBuffers ?? true;
    // Same party = same member OR same organisation (R6).
    const isSameMember = booking.memberId === memberId || (sameOrgMemberIds?.has(booking.memberId) ?? false);

    const bookingBufferStart = hasBuffers ? bookingStartMinutes - BUFFER_MINUTES : bookingStartMinutes;
    const bookingBufferEnd = hasBuffers ? bookingEndMinutes + BUFFER_MINUTES : bookingEndMinutes;

    // Check for core time overlap (always a conflict)
    if (proposedStartMinutes < bookingEndMinutes && proposedEndMinutes > bookingStartMinutes) {
      return {
        hasCollision: true,
        collisionType: "core",
        conflictingBooking: booking,
      };
    }

    // For same member: Allow if proposed booking would be adjacent
    if (isSameMember) {
      const clashesIntoBuffer = proposedEndMinutes > bookingBufferStart && proposedEndMinutes <= bookingStartMinutes;
      if (clashesIntoBuffer) {
        continue; // Skip, this is allowed
      }

      const clashesAfterBuffer = proposedStartMinutes >= bookingEndMinutes && proposedStartMinutes < bookingBufferEnd;
      if (clashesAfterBuffer) {
        continue; // Skip, this is allowed
      }
    }

    // For different members: Check for buffer overlap
    if (!isSameMember && proposedBufferStart < bookingBufferEnd && proposedBufferEnd > bookingBufferStart) {
      return {
        hasCollision: true,
        collisionType: "buffer",
        conflictingBooking: booking,
      };
    }
  }

  return { hasCollision: false };
};

/**
 * Check for gaps created by the proposed booking on either side, returning one entry per side when triggered.
 *
 * Warning types & thresholds:
 *  - `continuous` (proposer owns the adjacent booking) — fires for any raw gap in
 *    `(0, CONTINUOUS_GAP_MAX_RAW_MINUTES]` (>0 and ≤90m). The server chains and bills these as one merged
 *    block regardless of buffers, so every billed gap is warned. Gaps over 90m are bookable by another
 *    member, so they are neither bridged/billed nor warned. Only a zero raw gap (butting) is silent.
 *    PHASE 1 EXTENSION: A co-member in the proposer's organisation also triggers a `continuous` warning
 *    for the same gap range, marked with `isSameOrg: true`. Note: billing/chaining for the org case is
 *    NOT yet implemented server-side (deferred to Phase 2) — this is warning-only.
 *  - `gap` (different member owns the adjacent booking, not in org) — fires for raw gaps in
 *    `(0, MIN_BOOKABLE_GAP_RAW_MINUTES)` (>0 and strictly <90) with usable space > 0. A gap of 90+ is
 *    bookable by another member (1h + two 15m buffers) so it does not warn.
 *
 * `gapRawMinutes` is the raw end-to-start span (recommended for UI display).
 * `gapUsableMinutes` is the buffer-to-buffer remainder (can be 0 or negative when buffers
 * consume the raw span — kept for backwards compatibility; not for UI).
 *
 * When `sameOrgMemberIds` is provided, a different member in that set triggers a "continuous" warning
 * and sets `isSameOrg: true` on the gap (Phase 1 warning-only; billing/chaining deferred to Phase 2).
 */
export const checkBookingGaps = (
  startTime: string,
  durationMinutes: number,
  bookings: Booking[],
  roomId: string,
  date: Date,
  proposerMemberId: string,
  hasBuffers: boolean = true,
  sameOrgMemberIds?: Set<string>,
): BookingGap[] => {
  const proposedStartMinutes = timeToMinutes(startTime);
  const proposedEndMinutes = proposedStartMinutes + durationMinutes;
  const proposedBufferStart = hasBuffers ? proposedStartMinutes - BUFFER_MINUTES : proposedStartMinutes;
  const proposedBufferEnd = hasBuffers ? proposedEndMinutes + BUFFER_MINUTES : proposedEndMinutes;

  const relevantBookings = bookings
    .filter((b) => {
      if (b.isCancelled || b.roomId !== roomId) return false;
      return occursOnDate(b, date);
    })
    .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  const gaps: BookingGap[] = [];

  const shouldWarn = (rawGap: number, usableGap: number, isContinuous: boolean): boolean => {
    if (rawGap <= 0) return false;
    // Same member (R11): the server chains the two bookings and bills the whole merged span
    // (including the gap) for any raw gap up to 90m — findConsecutiveBookingChains has no lower floor
    // and ignores buffers. Any billed (>0, ≤90m) gap costs the member money, so the continuous warning
    // fires across that range: if it's billed, it's warned. A gap over 90m is bookable by another member,
    // so it is not chained/billed and does not warn. usableGap is irrelevant (buffer-blind).
    if (isContinuous) return rawGap <= CONTINUOUS_GAP_MAX_RAW_MINUTES;
    // Different member (R10): a room-efficiency warning only, never billed. Suppressed when the two
    // bookings' 15-minute buffers butt (touch) or overlap (usableGap ≤ 0). Otherwise warn only when
    // the gap is too small for another member to book — strictly under 90 minutes (a 1h booking +
    // two 15-minute buffers). A gap of 90+ is bookable, so it does not warn.
    if (usableGap <= 0) return false;
    return rawGap < MIN_BOOKABLE_GAP_RAW_MINUTES;
  };

  // Filter on raw cores so the gap rule still fires even when buffers overlap (R6 same-owner
  // continuous-booking case). Buffer overlap with a different owner is blocked elsewhere by R5.
  const bookingsBefore = relevantBookings.filter((b) => {
    const bookingEndMinutes = endTimeToMinutes(b.endTime, b.startTime);
    return bookingEndMinutes <= proposedStartMinutes;
  });

  if (bookingsBefore.length > 0) {
    const closestBefore = bookingsBefore[bookingsBefore.length - 1];
    const beforeEndMinutes = endTimeToMinutes(closestBefore.endTime, closestBefore.startTime);
    const beforeBufferEnd = (closestBefore.hasBuffers ?? true) ? beforeEndMinutes + BUFFER_MINUTES : beforeEndMinutes;
    const rawGapBefore = proposedStartMinutes - beforeEndMinutes;
    const gapBefore = proposedBufferStart - beforeBufferEnd;
    const isSameMember = closestBefore.memberId === proposerMemberId;
    const isSameOrg =
      !isSameMember && !!sameOrgMemberIds && sameOrgMemberIds.has(closestBefore.memberId);
    const isContinuous = isSameMember || isSameOrg;

    if (shouldWarn(rawGapBefore, gapBefore, isContinuous)) {
      gaps.push({
        direction: "before",
        gapRawMinutes: rawGapBefore,
        gapUsableMinutes: gapBefore,
        adjacentBooking: closestBefore,
        warningType: isContinuous ? "continuous" : "gap",
        isSameOrg,
      });
    }
  }

  const bookingsAfter = relevantBookings.filter((b) => {
    const bookingStartMinutes = timeToMinutes(b.startTime);
    return bookingStartMinutes >= proposedEndMinutes;
  });

  if (bookingsAfter.length > 0) {
    const closestAfter = bookingsAfter[0];
    const afterStartMinutes = timeToMinutes(closestAfter.startTime);
    const afterBufferStart = (closestAfter.hasBuffers ?? true) ? afterStartMinutes - BUFFER_MINUTES : afterStartMinutes;
    const rawGapAfter = afterStartMinutes - proposedEndMinutes;
    const gapAfter = afterBufferStart - proposedBufferEnd;
    const isSameMember = closestAfter.memberId === proposerMemberId;
    const isSameOrg =
      !isSameMember && !!sameOrgMemberIds && sameOrgMemberIds.has(closestAfter.memberId);
    const isContinuous = isSameMember || isSameOrg;

    if (shouldWarn(rawGapAfter, gapAfter, isContinuous)) {
      gaps.push({
        direction: "after",
        gapRawMinutes: rawGapAfter,
        gapUsableMinutes: gapAfter,
        adjacentBooking: closestAfter,
        warningType: isContinuous ? "continuous" : "gap",
        isSameOrg,
      });
    }
  }

  return gaps;
};

/**
 * Legacy single-gap shape retained for existing callers (old RepresentativeBookingFlow). New callers should prefer
 * checkBookingGaps which returns the full list and the per-gap warning type.
 */
export const checkUnbookableGap = (
  startTime: string,
  durationMinutes: number,
  bookings: Booking[],
  roomId: string,
  date: Date,
): UnbookableGapCheck => {
  const gaps = checkBookingGaps(startTime, durationMinutes, bookings, roomId, date, "", true);
  if (gaps.length === 0) return { hasGap: false };

  const first = gaps[0];
  return {
    hasGap: true,
    gapDurationMinutes: first.gapUsableMinutes,
    gapBefore: first.direction === "before" ? first.adjacentBooking : undefined,
    gapAfter: first.direction === "after" ? first.adjacentBooking : undefined,
  };
};

/**
 * Check recurring availability for the next 12 weeks
 * Returns dates where conflicts exist
 */
export const checkRecurringAvailability = async (
  roomId: string,
  startDate: Date,
  startTime: string,
  durationMinutes: number,
  memberId: string,
): Promise<RecurringAvailabilityCheck> => {
  const bookingService = new BookingService();
  const WEEKS_TO_CHECK = 12;

  const result = await bookingService.checkRecurringAvailability(
    roomId,
    startDate,
    startTime,
    durationMinutes,
    WEEKS_TO_CHECK,
    memberId,
  );

  return {
    totalWeeks: WEEKS_TO_CHECK,
    availableWeeks: result.availableCount,
    conflictDates: result.conflicts,
  };
};
